import { applyDatabinding } from '@dynamic/ui';
// eslint-disable-next-line no-restricted-imports -- tb implementation needs access to t
import i18next, { InitOptions, ResourceKey, t } from 'i18next';
import { proxy, snapshot, useSnapshot } from 'valtio';

import { configService, localeService } from '#/client/api';
import { Locale } from '#/client/models';
import { devtoolsApi } from '#/dev/devtools';
import { MenuConfig } from '#/service/config/config.types';
import { prepareMenu } from '#/service/config/transform';
import { StorageKeys } from '#/service/constants';
import { ignoreError } from '#/service/utils';

import { resetStore } from '../common';
import {
  detectLocale,
  fetchLocale,
  fetchLocales,
  getClientLocale,
} from './utils';

interface TranslationStore {
  translations: ResourceKey;
  locale: string;
  locales: Locale[];
  translatedMenu?: MenuConfig;
}

export const translationStore = proxy<TranslationStore>({
  translations: {},
  locale: getClientLocale(),
  locales: [],
  translatedMenu: undefined,
});

const initialTranslationStore = snapshot(translationStore) as TranslationStore;

export const translationActions = {
  async load(i18nextConfig?: InitOptions) {
    if (!i18nextConfig) {
      return;
    }

    // fetch list of locales from one-core
    const locales = await fetchLocales();
    await i18next.init({
      ...i18nextConfig,
      resources: {
        ...i18nextConfig.resources,
      },
    });
    // initialize the users' locale.
    const locale = detectLocale(locales);
    translationActions.changeLocale(locale?.code, true).catch(ignoreError);
    translationStore.locales = locales;
  },

  async changeLocale(locale: string, isInitialLoad: boolean = false) {
    // not the cleanest, but i18next recommends for checking which locales have been added already: https://github.com/i18next/i18next/issues/1068#issuecomment-400573808
    const loadedLanguages = Object.keys(
      i18next.services?.resourceStore?.data || {}
    );

    if (!loadedLanguages.includes(locale)) {
      const translation = await fetchLocale(locale);

      if (!translation) {
        return;
      }

      i18next.addResourceBundle(locale, 'translation', translation, true, true);
      i18next.loadLanguages([locale]).catch(ignoreError);
    }

    i18next.changeLanguage(locale).catch(ignoreError);

    translationStore.translations =
      i18next.services?.resourceStore?.data[locale].translation;
    translationStore.locale = locale;
    localStorage.setItem(StorageKeys.LOCALE, locale);

    if (!isInitialLoad) {
      localeService.setLocale(locale).catch(ignoreError);
    }

    // we don't need to load the menu for the initial language, because we have it already.
    // and for `cimode` we want to use translation keys, not real translations.
    if (isInitialLoad || locale === 'cimode') {
      translationStore.translatedMenu = undefined;
    } else {
      const translatedMenus = await configService.getMenuConfig();
      translationStore.translatedMenu = prepareMenu(translatedMenus.data);
    }
  },

  reset() {
    resetStore(translationStore, initialTranslationStore);
  },
};

export const tb = (value?: string | string[], bindingContext?: object) => {
  return value || value?.length
    ? applyDatabinding({
        value: t(value, bindingContext),
        context: {
          translations: translationStore.translations,
          ...bindingContext,
        },
      })
    : '';
};

export const useTranslationStore = () =>
  useSnapshot(translationStore) as TranslationStore;

devtoolsApi.setStore('translation', translationStore, translationActions);
